import { useMemo, useState, useCallback } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useNavigate } from 'react-router-dom';
import { estadoNormalizado } from '@/utils/estadoUtils';
import DetalhesFormacaoModal from '@/pages/formacao/acoes/components/DetalhesFormacaoModal';

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const ESTADO_STYLE: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  concluida:   { label: 'Concluída',  dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700' },
  em_curso:    { label: 'Em Curso',   dot: 'bg-amber-400',   bg: 'bg-amber-50',    text: 'text-amber-700' },
  planeada:    { label: 'Planeada',   dot: 'bg-zinc-400',    bg: 'bg-zinc-100',    text: 'text-zinc-600' },
  cancelada:   { label: 'Cancelada',  dot: 'bg-red-400',     bg: 'bg-red-50',      text: 'text-red-600' },
};

// Configuração do dropdown de estado (valores reais da base de dados)
const ESTADO_DROPDOWN: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  'Concluída':  { label: 'Concluída',  bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Em Curso':   { label: 'Em Curso',   bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  'Planeada':   { label: 'Planeada',   bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  'Cancelada':  { label: 'Cancelada',  bg: 'bg-rose-50',    text: 'text-rose-600',    dot: 'bg-rose-400'    },
};

const CAT_COLORS: Record<string, string> = {
  'Segurança':                  '#f59e0b',
  'Competências Transversais':  '#8b5cf6',
  'Jurídica e Administrativa':  '#3b82f6',
  'Informática':                '#06b6d4',
  'Ambiente':                   '#22c55e',
  'Gestão e Liderança':         '#f97316',
  'Técnica':                    '#64748b',
  'Comunicação':                '#ec4899',
  'Financeira':                 '#10b981',
  'Recursos Humanos':           '#a855f7',
};

function getMesIndex(dateStr: string | null): number {
  if (!dateStr) return -1;
  return new Date(dateStr).getMonth();
}

function catColor(cat: string | null): string {
  if (!cat) return '#94a3b8';
  for (const key of Object.keys(CAT_COLORS)) {
    if (cat.toLowerCase().includes(key.toLowerCase())) return CAT_COLORS[key];
  }
  return '#94a3b8';
}

// ── Resumo consolidado por ano ────────────────────────────────────────────────
interface ResumoAnoProps {
  anos: number[];
  formacoes: ReturnType<typeof useFormacao>['formacoes'];
  participantes: ReturnType<typeof useFormacao>['participantes'];
  anoSel: number;
  onSelectAno: (a: number) => void;
}

function ResumoConsolidado({ anos, formacoes, participantes, anoSel, onSelectAno }: ResumoAnoProps) {
  const resumos = useMemo(() => {
    return anos.map((ano) => {
      const fs = formacoes.filter((f) => f.data_inicio && new Date(f.data_inicio).getFullYear() === ano);
      const concluidas = fs.filter((f) => estadoNormalizado(f.estado) === 'concluida').length;
      const horas = fs.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
      const idsFs = new Set(fs.map((f) => f.id));
      const parts = participantes.filter((p) => idsFs.has(p.formacao_id));
      const formandos = new Set(parts.map((p) => p.funcionario_id)).size;
      const taxaExec = fs.length > 0 ? Math.round((concluidas / fs.length) * 100) : 0;
      return { ano, total: fs.length, concluidas, horas, formandos, taxaExec };
    });
  }, [anos, formacoes, participantes]);

  if (resumos.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-zinc-100">
        <p className="text-[13px] font-bold text-zinc-800">Resumo Consolidado por Ano</p>
        <p className="text-[11px] text-zinc-400 mt-0.5">Visão rápida antes de entrar nos separadores</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-50 bg-zinc-50/50">
              <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Ano</th>
              <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Formações</th>
              <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Horas Previstas</th>
              <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Formandos</th>
              <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Taxa Execução</th>
              <th className="px-5 py-2.5 text-right text-[10px] font-semibold text-zinc-400 uppercase tracking-wide"></th>
            </tr>
          </thead>
          <tbody>
            {resumos.map((r) => (
              <tr
                key={r.ano}
                className={`border-b border-zinc-50 transition-colors cursor-pointer ${anoSel === r.ano ? 'bg-zinc-50' : 'hover:bg-zinc-50/60'}`}
                onClick={() => onSelectAno(r.ano)}
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-bold text-zinc-900">{r.ano}</span>
                    {anoSel === r.ano && (
                      <span className="text-[10px] font-bold bg-zinc-900 text-white px-1.5 py-0.5 rounded-full">Selecionado</span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-center">
                  <span className="text-[13px] font-bold text-zinc-800">{r.total}</span>
                  <span className="text-[10px] text-zinc-400 ml-1">({r.concluidas} concl.)</span>
                </td>
                <td className="px-5 py-3 text-center">
                  <span className="text-[13px] font-bold text-zinc-800">{r.horas}h</span>
                </td>
                <td className="px-5 py-3 text-center">
                  <span className="text-[13px] font-bold text-zinc-800">{r.formandos}</span>
                </td>
                <td className="px-5 py-3 text-center">
                  <div className="flex items-center gap-2 justify-center">
                    <div className="w-16 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${r.taxaExec}%`, backgroundColor: r.taxaExec >= 80 ? '#10b981' : r.taxaExec >= 50 ? '#f59e0b' : '#ef4444' }}
                      />
                    </div>
                    <span className={`text-[11px] font-bold ${r.taxaExec >= 80 ? 'text-emerald-600' : r.taxaExec >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {r.taxaExec}%
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); onSelectAno(r.ano); }}
                    className="whitespace-nowrap text-[10px] font-semibold text-zinc-400 hover:text-zinc-700 cursor-pointer"
                  >
                    Ver detalhes →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PlanoFormacaoPage() {
  const navigate = useNavigate();
  const { formacoes, participantes, criarFormacao, loading, saving, editarFormacao } = useFormacao();
  const [anoSel, setAnoSel] = useState<number>(new Date().getFullYear());
  const [categoriaSel, setCategoriaSel] = useState<string>('Todas');
  const [estadoFiltro, setEstadoFiltro] = useState<string>('Todos');
  const [mesFiltro, setMesFiltro] = useState<number | null>(null);
  const [criandoFormacao, setCriandoFormacao] = useState(false);
  const [estadoDropdownId, setEstadoDropdownId] = useState<string | null>(null);
  const [formacaoDetalhes, setFormacaoDetalhes] = useState<ReturnType<typeof useFormacao>['formacoes'][0] | null>(null);
  const [exportando, setExportando] = useState(false);

  const hoje = useMemo(() => new Date(), []);

  // Anos disponíveis
  const anos = useMemo(() => {
    const set = new Set<number>();
    formacoes.forEach((f) => {
      if (f.data_inicio) set.add(new Date(f.data_inicio).getFullYear());
    });
    const anoAtual = new Date().getFullYear();
    set.add(anoAtual);
    set.add(anoAtual + 1);
    return Array.from(set).sort();
  }, [formacoes]);

  // Categorias disponíveis
  const categorias = useMemo(() => {
    const set = new Set<string>();
    formacoes.forEach((f) => { if (f.categoria) set.add(f.categoria); });
    return ['Todas', ...Array.from(set).sort()];
  }, [formacoes]);

  // Formações do ano selecionado
  const formacoesAno = useMemo(() => {
    return formacoes.filter((f) => {
      const ano = f.data_inicio ? new Date(f.data_inicio).getFullYear() : null;
      const catOk = categoriaSel === 'Todas' || f.categoria === categoriaSel;
      const estadoOk = estadoFiltro === 'Todos' || estadoNormalizado(f.estado) === estadoNormalizado(estadoFiltro);
      const mesOk = mesFiltro === null || (f.data_inicio && new Date(f.data_inicio).getMonth() === mesFiltro);
      return ano === anoSel && catOk && estadoOk && mesOk;
    }).sort((a, b) => (a.data_inicio ?? '').localeCompare(b.data_inicio ?? ''));
  }, [formacoes, anoSel, categoriaSel, estadoFiltro, mesFiltro]);

  // KPIs do ano
  const kpis = useMemo(() => {
    const total = formacoesAno.length;
    const concluidas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'concluida').length;
    const planeadas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'planeada').length;
    const horas = formacoesAno.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
    const custo = formacoesAno.reduce((s, f) => s + (f.custo ?? 0), 0);
    const totalParticipacoes = formacoesAno.reduce((s, f) => {
      return s + participantes.filter((p) => p.formacao_id === f.id).length;
    }, 0);
    const pctExecucao = total > 0 ? Math.round((concluidas / total) * 100) : 0;
    return { total, concluidas, planeadas, horas, custo, totalParticipacoes, pctExecucao };
  }, [formacoesAno, participantes]);

  // Por categoria (do ano)
  const porCategoria = useMemo(() => {
    const map: Record<string, { acoes: number; horas: number; concluidas: number; participantes: number }> = {};
    formacoesAno.forEach((f) => {
      const cat = f.categoria ?? 'Sem categoria';
      if (!map[cat]) map[cat] = { acoes: 0, horas: 0, concluidas: 0, participantes: 0 };
      map[cat].acoes += 1;
      map[cat].horas += f.duracao_horas ?? 0;
      if (estadoNormalizado(f.estado) === 'concluida') map[cat].concluidas += 1;
      map[cat].participantes += participantes.filter((p) => p.formacao_id === f.id).length;
    });
    return Object.entries(map).sort((a, b) => b[1].horas - a[1].horas);
  }, [formacoesAno, participantes]);

  // Calendário: distribuição por mês
  const distribuicaoMes = useMemo(() => {
    const arr = Array(12).fill(0).map(() => ({ total: 0, concluidas: 0, horas: 0 }));
    // Usar todas as formações do ano (sem filtro de mês) para o gráfico
    formacoes
      .filter((f) => {
        const ano = f.data_inicio ? new Date(f.data_inicio).getFullYear() : null;
        const catOk = categoriaSel === 'Todas' || f.categoria === categoriaSel;
        const estadoOk = estadoFiltro === 'Todos' || estadoNormalizado(f.estado) === estadoNormalizado(estadoFiltro);
        return ano === anoSel && catOk && estadoOk;
      })
      .forEach((f) => {
        const m = getMesIndex(f.data_inicio);
        if (m >= 0) {
          arr[m].total += 1;
          if (estadoNormalizado(f.estado) === 'concluida') arr[m].concluidas += 1;
          arr[m].horas += f.duracao_horas ?? 0;
        }
      });
    return arr;
  }, [formacoes, anoSel, categoriaSel, estadoFiltro]);

  const maxMes = Math.max(...distribuicaoMes.map((m) => m.total), 1);

  // ── Alertas: formações que começam em breve ou estão Em Curso há mais de X dias ──
  const alertasPlano = useMemo(() => {
    const result: {
      id: string;
      tipo: 'breve' | 'stale';
      nome: string;
      dias: number;
      severidade: 'critico' | 'aviso' | 'info';
      mensagem: string;
    }[] = [];

    formacoes
      .filter((f) => {
        const ano = f.data_inicio ? new Date(f.data_inicio).getFullYear() : null;
        return ano === anoSel && estadoNormalizado(f.estado) !== 'cancelada';
      })
      .forEach((f) => {
        // Breve: formações que começam em ≤ 14 dias
        if (f.data_inicio && (estadoNormalizado(f.estado) === 'planeada' || estadoNormalizado(f.estado) === 'em_curso')) {
          const dataInicio = new Date(f.data_inicio);
          const diasAteInicio = Math.floor((dataInicio.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
          if (diasAteInicio >= 0 && diasAteInicio <= 14) {
            const sev = diasAteInicio <= 3 ? 'critico' : diasAteInicio <= 7 ? 'aviso' : 'info';
            result.push({
              id: `${f.id}-breve`,
              tipo: 'breve',
              nome: f.nome,
              dias: diasAteInicio,
              severidade: sev,
              mensagem: diasAteInicio === 0 ? 'Começa hoje' : diasAteInicio === 1 ? 'Começa amanhã' : `Começa em ${diasAteInicio} dias`,
            });
          }
        }
        // Stale: Em Curso há mais de 30 dias sem data_fim
        if (estadoNormalizado(f.estado) === 'em_curso' && f.data_inicio) {
          const dataInicio = new Date(f.data_inicio);
          const diasEmCurso = Math.floor((hoje.getTime() - dataInicio.getTime()) / (1000 * 60 * 60 * 24));
          if (diasEmCurso > 30 && (!f.data_fim || new Date(f.data_fim) < hoje)) {
            result.push({
              id: `${f.id}-stale`,
              tipo: 'stale',
              nome: f.nome,
              dias: diasEmCurso,
              severidade: diasEmCurso > 60 ? 'critico' : 'aviso',
              mensagem: `Em curso há ${diasEmCurso} dias — sem data de conclusão`,
            });
          }
        }
      });

    return result.sort((a, b) => {
      const ordem = { critico: 0, aviso: 1, info: 2 };
      return ordem[a.severidade] - ordem[b.severidade];
    });
  }, [formacoes, anoSel, hoje]);

  // ── Exportar plano anual como PDF ───────────────────────────────────────────
  function exportarPlanoPdf() {
    setExportando(true);
    setTimeout(() => {
      const totalPrevisto = formacoesAno.reduce((s, f) => s + (f.orcamento_previsto ?? 0), 0);
      const totalReal = formacoesAno.reduce((s, f) => s + (f.custo ?? 0), 0);
      const concluidas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'concluida');
      const planeadas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'planeada');
      const emCurso = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'em_curso');

      const baseCSS = `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; }
        .page { max-width: 900px; margin: 0 auto; padding: 32px 40px; }
        .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
        .header-title { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; color: #111; }
        .header-sub { font-size: 11px; color: #666; margin-top: 4px; }
        .header-meta { text-align: right; font-size: 10px; color: #999; }
        .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #333; margin: 20px 0 8px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        th { background: #f5f5f5; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #555; padding: 6px 8px; text-align: left; border-bottom: 1px solid #ddd; }
        td { font-size: 10px; padding: 5px 8px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
        tr:last-child td { border-bottom: none; }
        .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
        .kpi-box { background: #f8f8f8; border: 1px solid #e8e8e8; border-radius: 6px; padding: 12px; }
        .kpi-val { font-size: 20px; font-weight: 800; color: #111; }
        .kpi-lbl { font-size: 9px; color: #888; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; margin-top: 2px; }
        .badge { display: inline-block; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 3px; }
        .badge-green { background: #dcfce7; color: #166534; }
        .badge-zinc { background: #f4f4f5; color: #52525b; }
        .badge-amber { background: #fef3c7; color: #92400e; }
        .badge-rose { background: #fee2e2; color: #991b1b; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .font-bold { font-weight: 700; }
        .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e5e5; display: flex; justify-content: space-between; font-size: 9px; color: #aaa; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      `;

      const nowStr = new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });
      const formatEuro = (v: number) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

      const rows = formacoesAno.map((f) => {
        const nPart = participantes.filter((p) => p.formacao_id === f.id).length;
        const estStyle = estadoNormalizado(f.estado) === 'concluida' ? 'badge-green' : estadoNormalizado(f.estado) === 'em_curso' ? 'badge-amber' : estadoNormalizado(f.estado) === 'cancelada' ? 'badge-rose' : 'badge-zinc';
        const estLabel = f.estado || 'Planeada';
        return { ...f, nPart, estStyle, estLabel };
      });

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Plano de Formação ${anoSel}</title>
      <style>${baseCSS}</style></head><body><div class="page">
      <div class="header">
        <div>
          <div class="header-title">Plano de Formação ${anoSel}</div>
          <div class="header-sub">Plano anual de formação profissional</div>
        </div>
        <div class="header-meta">
          <div>Emitido em ${nowStr}</div>
          <div>${formacoesAno.length} ações · ${kpis.horas}h</div>
        </div>
      </div>

      <div class="kpi-grid">
        <div class="kpi-box"><div class="kpi-val">${formacoesAno.length}</div><div class="kpi-lbl">Ações Totais</div></div>
        <div class="kpi-box"><div class="kpi-val">${concluidas.length}</div><div class="kpi-lbl">Concluídas</div></div>
        <div class="kpi-box"><div class="kpi-val">${planeadas.length}</div><div class="kpi-lbl">Planeadas</div></div>
        <div class="kpi-box"><div class="kpi-val">${kpis.horas}h</div><div class="kpi-lbl">Horas Previstas</div></div>
      </div>

      <div class="section-title">Ações de Formação — ${anoSel}</div>
      <table>
        <thead><tr>
          <th>Ação</th>
          <th>Categoria</th>
          <th>Entidade</th>
          <th class="text-center">Horas</th>
          <th class="text-center">Part.</th>
          <th class="text-right">Custo</th>
          <th class="text-center">Estado</th>
        </tr></thead>
        <tbody>
          ${rows.map((r) => `<tr>
            <td class="font-bold">${r.nome}</td>
            <td>${r.categoria ?? '—'}</td>
            <td>${r.entidade_formadora ?? '—'}</td>
            <td class="text-center">${r.duracao_horas}h</td>
            <td class="text-center">${r.nPart}</td>
            <td class="text-right">${r.custo > 0 ? formatEuro(r.custo) : '—'}</td>
            <td class="text-center"><span class="badge ${r.estStyle}">${r.estLabel}</span></td>
          </tr>`).join('')}
          <tr style="background:#f8f8f8; font-weight:700;">
            <td colspan="3">TOTAL</td>
            <td class="text-center">${kpis.horas}h</td>
            <td class="text-center">${kpis.totalParticipacoes}</td>
            <td class="text-right">${formatEuro(totalReal)}</td>
            <td>${kpis.concluidas}/${kpis.total}</td>
          </tr>
        </tbody>
      </table>

      ${alertasPlano.length > 0 ? `<div class="section-title">Alertas do Plano</div>
      <table>
        <thead><tr><th>Ação</th><th>Tipo</th><th>Descrição</th></tr></thead>
        <tbody>
          ${alertasPlano.map((a) => `<tr>
            <td class="font-bold">${a.nome}</td>
            <td><span class="badge ${a.severidade === 'critico' ? 'badge-rose' : a.severidade === 'aviso' ? 'badge-amber' : 'badge-zinc'}">${a.tipo === 'breve' ? 'Breve' : 'Em Curso Prolongado'}</span></td>
            <td>${a.mensagem}</td>
          </tr>`).join('')}
        </tbody>
      </table>` : ''}

      <div class="footer">
        <div>Plano de Formação Municipal — Documento Confidencial</div>
        <div>Gerado automaticamente em ${nowStr}</div>
      </div>
      </div></body></html>`;

      const win = window.open('', '_blank');
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
        setTimeout(() => { win.focus(); win.print(); }, 800);
      }
      setExportando(false);
    }, 400);
  }

  // Criar formação pré-preenchida com o ano selecionado
  const handleCriarFormacao = useCallback(async () => {
    setCriandoFormacao(true);
    // Navegar para a página de ações com o ano pré-selecionado via query param
    navigate(`/formacao/acoes?ano=${anoSel}&nova=1`);
    setCriandoFormacao(false);
  }, [anoSel, navigate]);

  if (loading) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
          <p className="text-[13px] text-zinc-400">A carregar plano de formação...</p>
        </div>
      </main>
    );
  }

  const estadoOptions = [
    { value: 'Todos', label: 'Todos os estados' },
    { value: 'Planeada', label: 'Planeada' },
    { value: 'Em Curso', label: 'Em Curso' },
    { value: 'Concluída', label: 'Concluída' },
    { value: 'Cancelada', label: 'Cancelada' },
  ];

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-clipboard-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Plano de Formação</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">Plano anual de formação — execução, calendário e distribuição temática</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filtro ano */}
            <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 gap-0.5">
              {anos.map((a) => (
                <button
                  key={a}
                  onClick={() => setAnoSel(a)}
                  className={`px-3.5 py-1.5 rounded-md text-[12px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    anoSel === a ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
            {/* Filtro estado */}
            <select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              className="border border-zinc-200 rounded-lg text-[12px] text-zinc-700 px-3 py-2 bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-zinc-300"
            >
              {estadoOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {/* Filtro categoria */}
            <select
              value={categoriaSel}
              onChange={(e) => setCategoriaSel(e.target.value)}
              className="border border-zinc-200 rounded-lg text-[12px] text-zinc-700 px-3 py-2 bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-zinc-300"
            >
              {categorias.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {/* Botão nova formação pré-preenchida */}
            <button
              onClick={handleCriarFormacao}
              disabled={criandoFormacao}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
              Nova Formação {anoSel}
            </button>
            {/* Exportar plano */}
            <button
              onClick={exportarPlanoPdf}
              disabled={exportando || formacoesAno.length === 0}
              className="whitespace-nowrap flex items-center gap-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 bg-white px-4 py-2.5 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer disabled:opacity-50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {exportando ? <i className="ri-loader-4-line text-sm animate-spin" /> : <i className="ri-file-download-line text-sm" />}
              </div>
              {exportando ? 'A gerar...' : 'Exportar PDF'}
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">
        {/* ── Indicador visual de estado de execução do plano ── */}
        {formacoesAno.length > 0 && (
          <div className={`rounded-xl border p-4 flex items-center gap-4 ${
            kpis.pctExecucao === 100
              ? 'bg-emerald-50 border-emerald-200'
              : kpis.pctExecucao >= 70
                ? 'bg-teal-50 border-teal-200'
                : kpis.pctExecucao >= 30
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-rose-50 border-rose-200'
          }`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
              kpis.pctExecucao === 100
                ? 'bg-emerald-100'
                : kpis.pctExecucao >= 70
                  ? 'bg-teal-100'
                  : kpis.pctExecucao >= 30
                    ? 'bg-amber-100'
                    : 'bg-rose-100'
            }`}>
              <i className={`${
                kpis.pctExecucao === 100
                  ? 'ri-checkbox-circle-fill text-emerald-600'
                  : kpis.pctExecucao >= 70
                    ? 'ri-loader-2-line text-teal-600'
                    : kpis.pctExecucao >= 30
                      ? 'ri-time-fill text-amber-600'
                      : 'ri-alarm-warning-fill text-rose-600'
              } text-lg`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-[14px] font-bold ${
                  kpis.pctExecucao === 100
                    ? 'text-emerald-800'
                    : kpis.pctExecucao >= 70
                      ? 'text-teal-800'
                      : kpis.pctExecucao >= 30
                        ? 'text-amber-800'
                        : 'text-rose-800'
                }`}>
                  {kpis.pctExecucao === 100
                    ? 'Plano Concluído'
                    : kpis.pctExecucao >= 70
                      ? 'Plano em Bom Estado'
                      : kpis.pctExecucao >= 30
                        ? 'Plano em Progresso'
                        : 'Plano em Atraso'}
                </p>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                  kpis.pctExecucao === 100
                    ? 'bg-emerald-100 text-emerald-700'
                    : kpis.pctExecucao >= 70
                      ? 'bg-teal-100 text-teal-700'
                      : kpis.pctExecucao >= 30
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-rose-100 text-rose-700'
                }`}>
                  {kpis.pctExecucao}% executado
                </span>
              </div>
              <div className="mt-2">
                <div className="h-2 rounded-full bg-white/60 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${kpis.pctExecucao}%`,
                      backgroundColor: kpis.pctExecucao === 100 ? '#10b981' : kpis.pctExecucao >= 70 ? '#14b8a6' : kpis.pctExecucao >= 30 ? '#f59e0b' : '#ef4444',
                    }}
                  />
                </div>
                <p className={`text-[11px] mt-1 ${
                  kpis.pctExecucao === 100
                    ? 'text-emerald-600'
                    : kpis.pctExecucao >= 70
                      ? 'text-teal-600'
                      : kpis.pctExecucao >= 30
                        ? 'text-amber-600'
                        : 'text-rose-600'
                }`}>
                  {kpis.concluidas} de {kpis.total} ações concluídas · {kpis.planeadas} planeada{kpis.planeadas !== 1 ? 's' : ''} · {kpis.total - kpis.concluidas - kpis.planeadas} em curso
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Alertas automáticos no Plano ── */}
        {alertasPlano.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-amber-500 text-sm" />
              </div>
              <h2 className="text-[13px] font-bold text-zinc-800">Alertas do Plano {anoSel}</h2>
              <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{alertasPlano.length} alerta{alertasPlano.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
              {alertasPlano.slice(0, 6).map((alerta) => {
                const cfg = alerta.severidade === 'critico'
                  ? { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-700', icon: 'ri-alarm-warning-line', iconBg: 'bg-rose-100' }
                  : alerta.severidade === 'aviso'
                    ? { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: 'ri-error-warning-line', iconBg: 'bg-amber-100' }
                    : { bg: 'bg-zinc-50 border-zinc-200', text: 'text-zinc-600', icon: 'ri-information-line', iconBg: 'bg-zinc-100' };
                return (
                  <div key={alerta.id} className={`rounded-xl border p-3.5 ${cfg.bg}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${cfg.iconBg}`}>
                        <i className={`${cfg.icon} text-sm ${cfg.text}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-[12px] font-bold ${cfg.text} truncate flex-1`}>{alerta.nome}</p>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.iconBg} ${cfg.text} flex-shrink-0`}>
                            {alerta.tipo === 'breve' ? 'Breve' : 'Prolongado'}
                          </span>
                        </div>
                        <p className={`text-[11px] mt-0.5 ${cfg.text} opacity-80`}>{alerta.mensagem}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {alertasPlano.length > 6 && (
                <div className="flex items-center justify-center rounded-xl border border-zinc-100 p-3.5">
                  <span className="text-[12px] font-semibold text-zinc-400">+ {alertasPlano.length - 6} alertas adicionais</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Resumo consolidado por ano */}
        <ResumoConsolidado
          anos={anos}
          formacoes={formacoes}
          participantes={participantes}
          anoSel={anoSel}
          onSelectAno={setAnoSel}
        />

        {/* Filtros ativos */}
        {(estadoFiltro !== 'Todos' || mesFiltro !== null || categoriaSel !== 'Todas') && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-zinc-400">Filtros ativos:</span>
            {estadoFiltro !== 'Todos' && (
              <span className="flex items-center gap-1 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1 rounded-full">
                {estadoOptions.find((o) => o.value === estadoFiltro)?.label}
                <button onClick={() => setEstadoFiltro('Todos')} className="whitespace-nowrap ml-1 cursor-pointer">×</button>
              </span>
            )}
            {mesFiltro !== null && (
              <span className="flex items-center gap-1 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1 rounded-full">
                {MESES[mesFiltro]}
                <button onClick={() => setMesFiltro(null)} className="whitespace-nowrap ml-1 cursor-pointer">×</button>
              </span>
            )}
            {categoriaSel !== 'Todas' && (
              <span className="flex items-center gap-1 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1 rounded-full">
                {categoriaSel}
                <button onClick={() => setCategoriaSel('Todas')} className="whitespace-nowrap ml-1 cursor-pointer">×</button>
              </span>
            )}
            <button
              onClick={() => { setEstadoFiltro('Todos'); setMesFiltro(null); setCategoriaSel('Todas'); }}
              className="whitespace-nowrap text-[11px] text-zinc-400 hover:text-zinc-700 cursor-pointer"
            >
              Limpar todos
            </button>
          </div>
        )}

        {formacoesAno.length === 0 && estadoFiltro === 'Todos' && mesFiltro === null && categoriaSel === 'Todas' ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-clipboard-line text-4xl text-zinc-200" />
            </div>
            <p className="text-[14px] font-semibold text-zinc-400">Sem ações de formação para {anoSel}</p>
            <p className="text-[12px] text-zinc-300">Crie ações de formação com datas em {anoSel} para que apareçam aqui.</p>
            <button
              onClick={handleCriarFormacao}
              className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
              Criar primeira formação de {anoSel}
            </button>
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Ações Totais',     value: kpis.total,               icon: 'ri-clipboard-line' },
                { label: 'Concluídas',        value: kpis.concluidas,          icon: 'ri-checkbox-circle-line' },
                { label: 'Planeadas',         value: kpis.planeadas,           icon: 'ri-calendar-schedule-line' },
                { label: 'Horas de Formação', value: `${kpis.horas}h`,         icon: 'ri-time-line' },
                { label: 'Participações',     value: kpis.totalParticipacoes,  icon: 'ri-user-star-line' },
                { label: 'Taxa Execução',     value: `${kpis.pctExecucao}%`,   icon: 'ri-bar-chart-grouped-line' },
              ].map((k) => (
                <div key={k.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-col gap-1.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-100">
                    <i className={`${k.icon} text-zinc-500 text-sm`} />
                  </div>
                  <p className="text-[22px] font-bold text-zinc-900 leading-none">{k.value}</p>
                  <p className="text-[11px] text-zinc-400 font-medium leading-tight">{k.label}</p>
                </div>
              ))}
            </div>

            {/* Barra de execução global */}
            <div className="bg-white rounded-xl border border-zinc-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[14px] font-bold text-zinc-800">Execução do Plano {anoSel}</p>
                  <p className="text-[12px] text-zinc-400 mt-0.5">{kpis.concluidas} de {kpis.total} ações concluídas</p>
                </div>
                <span className="text-[28px] font-black text-zinc-900">{kpis.pctExecucao}%</span>
              </div>
              <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-zinc-900 transition-all duration-700" style={{ width: `${kpis.pctExecucao}%` }} />
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                  <span className="text-[11px] text-zinc-500">Concluída ({kpis.concluidas})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-300 inline-block" />
                  <span className="text-[11px] text-zinc-500">Planeada ({kpis.planeadas})</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Calendário mensal clicável */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[14px] font-bold text-zinc-800">Distribuição Mensal — {anoSel}</p>
                  {mesFiltro !== null && (
                    <button
                      onClick={() => setMesFiltro(null)}
                      className="whitespace-nowrap text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer"
                    >
                      Limpar filtro mês ×
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-12 gap-1 items-end h-28">
                  {MESES.map((mes, i) => {
                    const d = distribuicaoMes[i];
                    const pct = d.total > 0 ? (d.total / maxMes) * 100 : 0;
                    const concPct = d.total > 0 ? (d.concluidas / d.total) * 100 : 0;
                    const isSelected = mesFiltro === i;
                    return (
                      <div
                        key={mes}
                        className="flex flex-col items-center gap-1 h-full justify-end group cursor-pointer"
                        onClick={() => setMesFiltro(mesFiltro === i ? null : i)}
                        title={d.total > 0 ? `${d.total} ação(ões) · ${d.horas}h · ${d.concluidas} concluída(s) — clica para filtrar` : 'Sem ações — clica para filtrar'}
                      >
                        <div
                          className="relative w-full rounded-t-sm overflow-hidden transition-all"
                          style={{
                            height: `${Math.max(pct, 4)}%`,
                            minHeight: d.total > 0 ? '8px' : '4px',
                            outline: isSelected ? '2px solid #18181b' : 'none',
                            outlineOffset: '1px',
                          }}
                        >
                          <div className={`absolute inset-0 rounded-t-sm transition-colors ${isSelected ? 'bg-zinc-300' : 'bg-zinc-200 group-hover:bg-zinc-300'}`} />
                          {d.total > 0 && (
                            <div
                              className="absolute bottom-0 left-0 right-0 rounded-t-sm transition-all"
                              style={{ height: `${concPct}%`, backgroundColor: '#10b981' }}
                            />
                          )}
                        </div>
                        <span className={`text-[9px] font-medium transition-colors ${isSelected ? 'text-zinc-900 font-bold' : 'text-zinc-400'}`}>{mes}</span>
                        {d.total > 0 && (
                          <span className={`text-[9px] font-bold ${isSelected ? 'text-zinc-900' : 'text-zinc-600'}`}>{d.total}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-50">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />
                    <span className="text-[11px] text-zinc-400">Concluída</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-zinc-200 inline-block" />
                    <span className="text-[11px] text-zinc-400">Planeada/Total</span>
                  </div>
                  <span className="text-[10px] text-zinc-400 ml-auto">Clica num mês para filtrar</span>
                </div>
              </div>

              {/* Por categoria */}
              <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between">
                  <p className="text-[13px] font-bold text-zinc-800">Por Área Temática</p>
                  <span className="text-[11px] text-zinc-400">{porCategoria.length} área(s)</span>
                </div>
                <div className="divide-y divide-zinc-50 max-h-64 overflow-y-auto">
                  {porCategoria.length === 0 ? (
                    <div className="px-5 py-8 text-center">
                      <p className="text-[12px] text-zinc-400">Sem dados para os filtros selecionados</p>
                    </div>
                  ) : porCategoria.map(([cat, v]) => {
                    const color = catColor(cat);
                    const pct = v.acoes > 0 ? Math.round((v.concluidas / v.acoes) * 100) : 0;
                    return (
                      <div key={cat} className="px-5 py-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                            <span className="text-[12px] font-semibold text-zinc-700 truncate">{cat}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="text-[10px] text-zinc-400">{v.acoes} ação(ões)</span>
                            <span className="text-[10px] font-bold" style={{ color }}>{pct}%</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] text-zinc-400">{v.horas}h</span>
                          <span className="text-[10px] text-zinc-400">{v.participantes} participações</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Lista de ações */}
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[14px] font-bold text-zinc-800">
                    Ações de Formação — {anoSel}
                    {mesFiltro !== null && <span className="ml-2 text-[12px] font-normal text-zinc-400">· {MESES[mesFiltro]}</span>}
                    {estadoFiltro !== 'Todos' && <span className="ml-2 text-[12px] font-normal text-zinc-400">· {estadoOptions.find((o) => o.value === estadoFiltro)?.label}</span>}
                  </p>
                  <p className="text-[12px] text-zinc-400 mt-0.5">{formacoesAno.length} ação(ões) · {kpis.horas} horas totais</p>
                </div>
                <button
                  onClick={handleCriarFormacao}
                  className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold text-zinc-500 hover:text-zinc-800 bg-zinc-100 hover:bg-zinc-200 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                  Nova formação {anoSel}
                </button>
              </div>

              {formacoesAno.length === 0 ? (
                <div className="py-12 flex flex-col items-center gap-2">
                  <i className="ri-search-line text-3xl text-zinc-200" />
                  <p className="text-[13px] text-zinc-400">Nenhuma formação com os filtros selecionados</p>
                  <button
                    onClick={() => { setEstadoFiltro('Todos'); setMesFiltro(null); setCategoriaSel('Todas'); }}
                    className="whitespace-nowrap text-[11px] font-semibold text-zinc-500 hover:text-zinc-700 cursor-pointer mt-1"
                  >
                    Limpar filtros
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-100 bg-zinc-50">
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Ação de Formação</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Área</th>
                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Período</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Horas</th>
                        <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Part.</th>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50">
                      {formacoesAno.map((f) => {
                        const estadoKey = estadoNormalizado(f.estado) as keyof typeof ESTADO_STYLE;
                        const es = ESTADO_STYLE[estadoKey] ?? ESTADO_STYLE.planeada;
                        const color = catColor(f.categoria);
                        const nPart = participantes.filter((p) => p.formacao_id === f.id).length;
                        const dataInicio = f.data_inicio
                          ? new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })
                          : '—';
                        const dataFim = f.data_fim
                          ? new Date(f.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })
                          : null;
                        const isDropdownOpen = estadoDropdownId === f.id;

                        return (
                          <tr key={f.id} onClick={() => setFormacaoDetalhes(f)} className="hover:bg-zinc-50/70 transition-colors cursor-pointer">
                            <td className="px-6 py-4">
                              <div className="flex items-start gap-3">
                                <div className="w-1 h-10 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: color }} />
                                <div className="min-w-0">
                                  <p className="text-[13px] font-semibold text-zinc-800 leading-tight">{f.nome}</p>
                                  {f.entidade_formadora && (
                                    <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{f.entidade_formadora}</p>
                                  )}
                                  {f.modalidade && (
                                    <span className="inline-block text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded mt-1 font-medium">{f.modalidade}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              {f.categoria && (
                                <span
                                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md"
                                  style={{ backgroundColor: `${color}18`, color }}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                  {f.categoria}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-[12px] font-semibold text-zinc-700 whitespace-nowrap">
                                {dataInicio}{dataFim && dataFim !== dataInicio ? ` – ${dataFim}` : ''}
                              </p>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className="text-[13px] font-bold text-zinc-700">{f.duracao_horas}h</span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className="text-[13px] font-bold text-zinc-700">
                                {nPart > 0 ? nPart : <span className="text-zinc-300">—</span>}
                              </span>
                            </td>
                            <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEstadoDropdownId(isDropdownOpen ? null : f.id);
                                  }}
                                  disabled={saving}
                                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${es.bg} ${es.text}`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${es.dot}`} />
                                  {es.label}
                                  <div className="w-3 h-3 flex items-center justify-center opacity-60">
                                    <i className="ri-arrow-down-s-line text-xs" />
                                  </div>
                                </button>
                                {isDropdownOpen && (
                                  <div className="absolute z-20 mt-1 left-0 bg-white border border-zinc-200 rounded-xl shadow-lg py-1 min-w-[140px]">
                                    {Object.entries(ESTADO_DROPDOWN).map(([key, cfg]) => (
                                      <button
                                        key={key}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setEstadoDropdownId(null);
                                          if (key !== f.estado) {
                                            await editarFormacao(f.id, { estado: key });
                                          }
                                        }}
                                        disabled={saving}
                                        className={`w-full text-left px-3 py-1.5 text-[11px] font-semibold flex items-center gap-2 hover:bg-zinc-50 transition-colors cursor-pointer ${f.estado === key ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                        <span className={cfg.text}>{cfg.label}</span>
                                        {f.estado === key && <i className="ri-check-line text-xs ml-auto" />}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-zinc-50 border-t border-zinc-200">
                      <tr>
                        <td className="px-6 py-3 text-[12px] font-bold text-zinc-600">Total {anoSel}</td>
                        <td className="px-4 py-3" />
                        <td className="px-4 py-3" />
                        <td className="px-4 py-3 text-center text-[13px] font-bold text-zinc-800">{kpis.horas}h</td>
                        <td className="px-4 py-3 text-center text-[13px] font-bold text-zinc-800">{kpis.totalParticipacoes}</td>
                        <td className="px-6 py-3 text-[12px] font-bold text-zinc-600">{kpis.concluidas}/{kpis.total} concluídas</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Modal de detalhes */}
            {formacaoDetalhes && (
              <DetalhesFormacaoModal
                formacao={formacaoDetalhes}
                onClose={() => setFormacaoDetalhes(null)}
              />
            )}
          </>
        )}
      </div>
    </main>
  );
}